import { useState, useEffect, useRef } from "react";
import { Star, ExternalLink, Quote, RefreshCcw, AlertCircle, ChevronLeft, ChevronRight } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Fallback static testimonials used when no live reviews are available
const FALLBACK_REVIEWS = [
  {
    author: { display_name: "Maria Johnson", photo_uri: null },
    rating: 5,
    text: "EZ Care Pharmacy is the best pharmacy I've ever been to. The staff is so friendly and always takes time to answer my questions. Free home delivery has been a lifesaver for my elderly mother!",
    relative_time: "2 months ago",
  },
  {
    author: { display_name: "Robert Williams", photo_uri: null },
    rating: 5,
    text: "I've been coming here for over two years. They always have my medications ready on time and the pharmacist remembers my name. This is what a real neighborhood pharmacy feels like.",
    relative_time: "3 months ago",
  },
  {
    author: { display_name: "Priya Patel", photo_uri: null },
    rating: 5,
    text: "As a new mom, I love that they carry everything from baby care to wellness products. The staff is incredibly knowledgeable and the medicine refill reminder service is so convenient!",
    relative_time: "1 month ago",
  },
  {
    author: { display_name: "Keisha Thompson", photo_uri: null },
    rating: 5,
    text: "It's so refreshing to have a women-owned pharmacy that truly cares about the community. The prices are fair and they go above and beyond every single time. Highly recommend!",
    relative_time: "2 weeks ago",
  },
  {
    author: { display_name: "David Chen", photo_uri: null },
    rating: 5,
    text: "The language assistance and wheelchair accessible entrance make this pharmacy truly inclusive. I feel welcome every time I visit. The free parking is a great bonus too!",
    relative_time: "5 weeks ago",
  },
];

function StarRow({ rating, size = 16 }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((s) => (
        <Star
          key={s}
          size={size}
          className={s <= rating ? "text-amber-400 fill-amber-400" : "text-gray-200 fill-gray-200"}
        />
      ))}
    </div>
  );
}

function AvatarInitial({ name, photoUri }) {
  const colors = [
    "bg-teal-500", "bg-blue-500", "bg-purple-500",
    "bg-pink-500", "bg-amber-500", "bg-emerald-500",
  ];
  const colorIdx = name.charCodeAt(0) % colors.length;
  const initials = name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();

  if (photoUri) {
    return (
      <img
        src={photoUri}
        alt={name}
        referrerPolicy="no-referrer"
        className="w-11 h-11 rounded-full object-cover border-2 border-white shadow"
      />
    );
  }

  return (
    <div className={`w-11 h-11 ${colors[colorIdx]} rounded-full flex items-center justify-center text-white font-bold text-sm font-outfit flex-shrink-0`}>
      {initials}
    </div>
  );
}

function ReviewCard({ review, index }) {
  const [expanded, setExpanded] = useState(false);
  const isLong = review.text.length > 140;
  const displayText = isLong && !expanded ? review.text.slice(0, 140) + "â€¦" : review.text;

  return (
    <div
      data-testid={`google-review-card-${index}`}
      className="bg-white rounded-2xl p-6 flex flex-col gap-4 shadow-[0_2px_8px_rgba(0,0,0,0.08)] hover:shadow-[0_8px_24px_rgba(190,10,117,0.16)] transition-all duration-300 hover:-translate-y-1"
    >
      {/* Quote icon */}
      <Quote size={22} className="text-ez-teal opacity-20" />

      {/* Review text */}
      <p className="text-ez-muted text-sm leading-relaxed font-jakarta flex-1">
        {displayText}
        {isLong && (
          <button
            onClick={() => setExpanded((e) => !e)}
            className="text-ez-teal font-semibold ml-1 text-xs hover:underline"
          >
            {expanded ? "Show less" : "Read more"}
          </button>
        )}
      </p>

      {/* Stars */}
      <StarRow rating={review.rating} />

      {/* Author */}
      <div className="flex items-center gap-3 pt-2 border-t border-gray-50">
        <AvatarInitial name={review.author.display_name} photoUri={review.author.photo_uri} />
        <div>
          <div className="font-semibold text-ez-text text-sm font-outfit">
            {review.author.display_name}
          </div>
          <div className="text-ez-muted text-xs font-jakarta">{review.relative_time}</div>
        </div>
        {/* Google G icon */}
        <div className="ml-auto opacity-40">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
          </svg>
        </div>
      </div>
    </div>
  );
}

export default function GoogleReviews() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [current, setCurrent] = useState(0);
  const ref = useRef(null);

  // Intersection observer for scroll animation
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add("visible")),
      { threshold: 0.1 }
    );
    const items = ref.current?.querySelectorAll(".animate-on-view");
    items?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [data]);

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/reviews`);
        setData(res.data);
      } catch (err) {
        console.error("Reviews fetch error:", err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    fetchReviews();
  }, []);

  // Determine which reviews to show
  const isLive = !error && data && data.reviews && data.reviews.length > 0;
  const reviews = isLive ? data.reviews : FALLBACK_REVIEWS;
  const overallRating = isLive ? data.overall_rating : 5.0;
  const totalRatings = isLive ? data.total_ratings : null;
  const googleMapsUrl = isLive ? data.google_maps_url : "https://www.google.com/maps/place/447+William+St,+East+Orange,+NJ+07017";

  const prev = () => setCurrent((c) => (c - 1 + reviews.length) % reviews.length);
  const next = () => setCurrent((c) => (c + 1) % reviews.length);

  // Visible cards: 3 at a time on desktop
  const getVisible = () => {
    const out = [];
    for (let i = 0; i < 3; i++) {
      out.push(reviews[(current + i) % reviews.length]);
    }
    return out;
  };

  return (
    <section
      id="testimonials"
      data-testid="google-reviews-section"
      className="ez-section bg-gradient-to-br from-ez-teal-dark via-teal-700 to-blue-800 overflow-hidden relative"
      ref={ref}
    >
      {/* Background blobs */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-white opacity-5 rounded-full translate-x-1/2 -translate-y-1/3 blur-3xl" />
      <div className="absolute bottom-0 left-0 w-80 h-80 bg-white opacity-5 rounded-full -translate-x-1/3 translate-y-1/3 blur-3xl" />

      <div className="ez-container relative z-10">

        {/* Header */}
        <div className="animate-on-view text-center mb-12">
          <div className="inline-block text-teal-200 font-semibold font-caveat text-lg mb-3">
            What Patients Say
          </div>
          <h2 className="font-outfit text-2xl md:text-3xl font-bold text-white mb-4">
            {isLive ? "Live Google Reviews" : "Community Voices"}
          </h2>

          {/* Overall rating badge */}
          {!loading && (
            <div className="inline-flex items-center gap-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl px-6 py-3 mt-2">
              <div className="text-3xl font-bold font-outfit text-white">
                {overallRating.toFixed(1)}
              </div>
              <div>
                <StarRow rating={Math.round(overallRating)} size={20} />
                {totalRatings && (
                  <div className="text-teal-200 text-xs mt-0.5 font-jakarta">
                    Based on {totalRatings.toLocaleString()} Google reviews
                  </div>
                )}
              </div>
              {isLive && (
                <a
                  href={googleMapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="view-on-google-link"
                  className="ml-2 flex items-center gap-1 text-teal-200 hover:text-white text-xs font-semibold transition-colors"
                >
                  <ExternalLink size={13} />
                  See all on Google
                </a>
              )}
            </div>
          )}

          {/* Loading spinner */}
          {loading && (
            <div className="flex items-center justify-center gap-2 text-teal-200 mt-4">
              <RefreshCcw size={16} className="animate-spin" />
              <span className="text-sm font-jakarta">Loading reviewsâ€¦</span>
            </div>
          )}
        </div>

        {/* Reviews Grid - Desktop 3-up */}
        {!loading && (
          <>
            <div className="hidden md:grid grid-cols-3 gap-6 mb-8">
              {getVisible().map((review, i) => (
                <div
                  key={`${review.author.display_name}-${i}`}
                  className={`animate-on-view transition-all duration-500 ${i === 1 ? "scale-105" : "scale-95 opacity-80"}`}
                  style={{ transitionDelay: `${i * 0.1}s` }}
                >
                  <ReviewCard review={review} index={i} />
                </div>
              ))}
            </div>

            {/* Mobile: single card */}
            <div className="md:hidden mb-8">
              <ReviewCard review={reviews[current]} index={0} />
            </div>

            {/* Controls */}
            <div className="flex items-center justify-center gap-4">
              <button
                data-testid="reviews-prev"
                onClick={prev}
                className="w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 text-white flex items-center justify-center transition-colors"
              >
                <ChevronLeft size={18} />
              </button>

              <div className="flex gap-2">
                {reviews.map((_, i) => (
                  <button
                    key={i}
                    data-testid={`reviews-dot-${i}`}
                    onClick={() => setCurrent(i)}
                    className={`h-2 rounded-full transition-all duration-300 ${i === current ? "w-6 bg-white" : "w-2 bg-white/40"}`}
                  />
                ))}
              </div>

              <button
                data-testid="reviews-next"
                onClick={next}
                className="w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 text-white flex items-center justify-center transition-colors"
              >
                <ChevronRight size={18} />
              </button>
            </div>

            {/* Source badge */}
            <div className="text-center mt-8">
              {isLive ? (
                <a
                  href={googleMapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="leave-review-link"
                  className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white text-sm font-semibold px-6 py-3 rounded-full transition-all duration-200 border border-white/20"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="flex-shrink-0">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                  </svg>
                  Leave a Review on Google
                  <ExternalLink size={14} />
                </a>
              ) : (
                <p className="text-teal-300 text-sm font-jakarta italic">
                  Featured voices from our community
                </p>
              )}
            </div>
          </>
        )}
      </div>
    </section>
  );
}

